import axios from 'axios'

/**
 * API Client - HTTP client with JWT token management
 * Mirrors the Flutter ApiClient architecture
 */
class ApiClient {
  constructor() {
    // Base URL - empty for local dev (uses Vite proxy), set VITE_API_URL for production
    this.baseURL = import.meta.env.VITE_API_URL ?? ''

    // Module name for X-Module header (set via VITE_MODULE env var)
    this.moduleName = import.meta.env.VITE_MODULE || ''

    // Create axios instance
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        ...(this.moduleName && { 'X-Module': this.moduleName }),
      },
    })

    // Request interceptor - inject JWT token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getToken()
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor - unwrap standardized responses and handle RFC 9457 errors
    this.client.interceptors.response.use(
      (response) => {
        // Check X-License-Status header on successful responses (for expired license read-only mode)
        const licenseStatus = response.headers?.['x-license-status']
        const licenseModule = response.headers?.['x-license-module']
        if (licenseStatus === 'expired') {
          // Emit event for UI to show warning (read-only mode)
          window.dispatchEvent(new CustomEvent('license-warning', {
            detail: { status: 'expired', module: licenseModule }
          }))
        }

        // Unwrap standardized response format: { data, meta, pagination? }
        // Return the full response to preserve pagination metadata
        return response
      },
      (error) => {
        if (error.response?.status === 401) {
          // Unauthorized - clear token and redirect to login
          // But NOT for login requests (they return 401 for invalid credentials)
          // And NOT if we're already on the login page (prevents redirect loop)
          const isLoginRequest = error.config?.url?.includes('/auth/login')
          const isOnLoginPage = window.location.pathname === '/login'
          if (!isLoginRequest && !isOnLoginPage) {
            this.clearToken()
            window.location.href = '/login'
          }
        }

        // Transform RFC 9457 Problem Details error format
        if (error.response?.data?.type && error.response?.data?.title) {
          // RFC 9457 format detected
          const problemDetail = error.response.data
          const enhancedError = new Error(problemDetail.detail || problemDetail.title)
          enhancedError.type = problemDetail.type
          enhancedError.title = problemDetail.title
          enhancedError.status = problemDetail.status
          enhancedError.detail = problemDetail.detail
          enhancedError.errors = problemDetail.errors // Field-level validation errors
          enhancedError.meta = problemDetail.meta

          // Add license-specific error properties from extensions
          // Note: Backend merges extension fields directly into top-level JSON
          const errorCode = problemDetail.error_code || problemDetail.extensions?.error_code || problemDetail.meta?.extensions?.error_code
          if (errorCode) {
            enhancedError.errorCode = errorCode
            enhancedError.isLicenseError = ['LICENSE_EXPIRED', 'NO_LICENSE', 'ALL_LICENSES_EXPIRED', 'NO_ACCESS'].includes(errorCode)
            enhancedError.module = problemDetail.module || problemDetail.extensions?.module
            enhancedError.action = problemDetail.action || problemDetail.extensions?.action
            enhancedError.readOnly = problemDetail.read_only || problemDetail.extensions?.read_only

            // Emit license error event for global handling
            if (enhancedError.isLicenseError) {
              window.dispatchEvent(new CustomEvent('license-error', {
                detail: {
                  errorCode: enhancedError.errorCode,
                  message: enhancedError.detail,
                  module: enhancedError.module,
                  action: enhancedError.action,
                  readOnly: enhancedError.readOnly
                }
              }))
            }
          }

          return Promise.reject(enhancedError)
        }

        return Promise.reject(error)
      }
    )
  }

  /**
   * Save JWT token to localStorage.
   *
   * If a different token is already present (e.g. from a prior session that
   * wasn't cleanly logged out), remove it first. This avoids a transient
   * state where two different tokens could both appear valid — critical for
   * multi-tenant hygiene when users switch between business accounts.
   */
  saveToken(token) {
    const existing = localStorage.getItem('auth_token')
    if (existing && existing !== token) {
      localStorage.removeItem('auth_token')
    }
    localStorage.setItem('auth_token', token)
  }

  /**
   * Get JWT token from localStorage
   */
  getToken() {
    return localStorage.getItem('auth_token')
  }

  /**
   * Clear JWT token from localStorage
   */
  clearToken() {
    localStorage.removeItem('auth_token')
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!this.getToken()
  }

  /**
   * HTTP GET request
   * Returns unwrapped data from standardized response: { data, meta, pagination? }
   */
  async get(url, config = {}) {
    const response = await this.client.get(url, config)
    // For list endpoints with pagination, return full response body
    if (response.data.pagination) {
      return response.data // { data, pagination, meta }
    }
    // For single-item endpoints, unwrap the data field if it exists
    // Otherwise return the full response body (handles non-standardized responses like { business: {...} })
    return response.data.data !== undefined ? response.data.data : response.data
  }

  /**
   * HTTP POST request
   * Returns unwrapped data from standardized response: { data, meta }
   */
  async post(url, data = {}, config = {}) {
    const response = await this.client.post(url, data, config)
    return response.data.data
  }

  /**
   * HTTP PUT request
   * Returns unwrapped data from standardized response: { data, meta }
   */
  async put(url, data = {}, config = {}) {
    const response = await this.client.put(url, data, config)
    return response.data.data
  }

  /**
   * HTTP PATCH request
   * Returns unwrapped data from standardized response: { data, meta }
   */
  async patch(url, data = {}, config = {}) {
    const response = await this.client.patch(url, data, config)
    return response.data?.data || response.data
  }

  /**
   * HTTP DELETE request
   * Returns unwrapped data from standardized response (if any)
   */
  async delete(url, config = {}) {
    const response = await this.client.delete(url, config)
    // DELETE typically returns 204 No Content, but if there's data, unwrap it
    return response.data?.data || response.data
  }

  /**
   * HTTP POST request for file uploads (multipart/form-data)
   * Returns unwrapped data from standardized response
   */
  async upload(url, formData, config = {}) {
    const response = await this.client.post(url, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data.data
  }
}

// Export singleton instance
export const apiClient = new ApiClient()
